// Generated types for SoftwarePart
// This file is auto-generated - do not edit manually

import { z } from 'zod';

import { SoftwareNature, SoftwareStatus } from '../enums';

import { PartReadAPI, PartWriteAPI } from './part';
import { PartReadAPISchemaObject, PartWriteAPISchemaObject } from './part';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface SoftwarePartReadAPI extends Omit<PartReadAPI, 'description'> {
  /** What this SoftwarePart is (the application / service / library / module it realises) and the role it plays. Required when authoring via the agent (LLM tool surface); optional for direct REST callers. */
  description?: string | null;
  /** Who owns / vendors this software (organisation, team or upstream project). */
  maintainer?: string | null;
  /** Ecosystem + package name, e.g. 'pip:sourcepilot', 'npm:@sp/ui'. */
  package_coordinates?: string | null;
  /** Link to documentation. */
  docs_url?: string | null;
  /** Licence cost or build-effort estimate. */
  cost?: number | null;
  /** Realisation lifecycle: PROPOSED / IN_DEVELOPMENT / RELEASED / DEPRECATED. */
  maturity_status?: SoftwareStatus;
  /** Artifact kind: APPLICATION / SERVICE / LIBRARY / MODULE / TEST_SUITE / CONFIG. */
  nature?: SoftwareNature;
  /** Where this component's code lives in its repository: directories or files, e.g. ['src/motor/', 'proto/motor.proto']; empty = the whole repository. */
  repo_paths?: string[];
  /** The commit SHA or tag the model considers current for this component. */
  pinned_ref?: string | null;
}

export interface SoftwarePartWriteAPI extends Omit<PartWriteAPI, 'description'> {
  /** What this SoftwarePart is (the application / service / library / module it realises) and the role it plays. Required when authoring via the agent (LLM tool surface); optional for direct REST callers. */
  description?: string | null;
  /** Who owns / vendors this software (organisation, team or upstream project). */
  maintainer?: string | null;
  /** Ecosystem + package name, e.g. 'pip:sourcepilot', 'npm:@sp/ui'. */
  package_coordinates?: string | null;
  /** Link to documentation. */
  docs_url?: string | null;
  /** Licence cost or build-effort estimate. */
  cost?: number | null;
  /** Realisation lifecycle: PROPOSED / IN_DEVELOPMENT / RELEASED / DEPRECATED. */
  maturity_status?: SoftwareStatus;
  /** Artifact kind: APPLICATION / SERVICE / LIBRARY / MODULE / TEST_SUITE / CONFIG. */
  nature?: SoftwareNature;
  parent?: string;
  children?: (string | SoftwarePartWriteAPI)[];
  /** Where this component's code lives in its repository: directories or files, e.g. ['src/motor/', 'proto/motor.proto']; empty = the whole repository. */
  repo_paths?: string[];
  /** The commit SHA or tag the model considers current for this component. */
  pinned_ref?: string | null;
  /** The connected GitHub repository this SoftwarePart is realized by (an id of one of the organisation's connected repos). Leave unset on a child to inherit its parent's. */
  realized_by_repo?: string;
}

// ============================================================================
// Zod Schemas
// ============================================================================

export const SoftwarePartReadAPISchema: z.ZodType<SoftwarePartReadAPI> = PartReadAPISchemaObject.omit({ 'description': true }).extend({
  description: z.string().nullable().optional(),
  maintainer: z.string().nullable().optional(),
  package_coordinates: z.string().nullable().optional(),
  docs_url: z.string().nullable().optional(),
  cost: z.number().nullable().optional(),
  maturity_status: z.nativeEnum(SoftwareStatus).optional(),
  nature: z.nativeEnum(SoftwareNature).optional(),
  repo_paths: z.array(z.string()).optional(),
  pinned_ref: z.string().nullable().optional(),
}).passthrough();

export const SoftwarePartWriteAPISchema: z.ZodType<SoftwarePartWriteAPI> = PartWriteAPISchemaObject.omit({ 'description': true }).extend({
  description: z.string().nullable().optional(),
  maintainer: z.string().nullable().optional(),
  package_coordinates: z.string().nullable().optional(),
  docs_url: z.string().nullable().optional(),
  cost: z.number().nullable().optional(),
  maturity_status: z.nativeEnum(SoftwareStatus).optional(),
  nature: z.nativeEnum(SoftwareNature).optional(),
  parent: z.string().optional(),
  children: z.array(z.union([z.string(), z.lazy(() => SoftwarePartWriteAPISchema)])).optional(),
  repo_paths: z.array(z.string()).optional(),
  pinned_ref: z.string().nullable().optional(),
  realized_by_repo: z.string().optional(),
}).passthrough();
